import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: block;
    }

    /* ── Container ──────────────────────────────────────────────────────── */
    .uwc-pg {
      display:         flex;
      align-items:     center;
      flex-wrap:       wrap;
      gap:             ${t.space1};
      padding:         var(--uwc-paginator-padding, ${t.space2} ${t.space4});
      background:      var(--uwc-paginator-bg, ${t.surface});
      border-top:      var(--uwc-paginator-border, 1px solid rgba(0,0,0,0.08));
      border-radius:   var(--uwc-paginator-radius, ${t.radiusMd});
      font-family:     var(--uwc-font-family, inherit);
      font-size:       var(--uwc-paginator-font-size, ${t.fontSizeMd});
      color:           ${t.text};
      box-sizing:      border-box;
      user-select:     none;
    }

    /* Spacer between left slot, page controls, and right slot */
    .uwc-pg__spacer { flex: 1; }

    /* Inline group wrapping a cluster of nav / page buttons */
    .uwc-pg__group {
      display:     inline-flex;
      align-items: center;
      gap:         0.125rem;
    }

    /* ── Buttons ────────────────────────────────────────────────────────────
       Nav + page controls are <uwc-button size="small">, so they share the
       control-height ladder with uwc-dropdown and rescale together under the
       Theme Designer. Only paginator-specific sizing lives here — colour and
       height come from the button's own variants/tokens.
       (Nav = text + secondary icon-only; active page = filled primary.) */
    /* Square page buttons. We style the button's exposed part directly and use
       aspect-ratio — the SAME mechanism the icon-only nav buttons rely on — so
       the width is transferred from whatever the height actually resolves to.
       That keeps it square at every Theme Designer scale without hardcoding (or
       even matching) a height value: a min-width can't do this because the inner
       button is width:100% + content-sized, and --uwc-btn-padding-x is ignored
       at size="small". Trimmed side padding keeps short numbers from forcing
       extra width; longer labels still grow past square. */
    .uwc-pg__page::part(button) {
      aspect-ratio:   1 / 1;
      padding-inline: 0.25rem;
    }

    /* Ellipsis — sized off the same control-height token as the page buttons */
    .uwc-pg__ellipsis {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      min-width:       ${t.controlHeightSm};
      height:          ${t.controlHeightSm};
      color:           ${t.textMuted};
      font-size:       inherit;
      letter-spacing:  0.1em;
    }

    /* ── Rows-per-page select ───────────────────────────────────────────── */
    .uwc-pg__rpp {
      display:       flex;
      align-items:   center;
      gap:           0.375rem;
      margin-inline: 0.375rem;
    }
    .uwc-pg__rpp-label {
      font-size:  var(--uwc-paginator-label-size, 0.8125rem);
      color:      ${t.textSecondary};
      white-space: nowrap;
    }
    /* Compact uwc-dropdown — overrides the dropdown's default 140px min width */
    .uwc-pg__rpp-select {
      --uwc-dd-trigger-min-width: 4.5rem;
    }

    /* ── Current page report ────────────────────────────────────────────── */
    .uwc-pg__report {
      font-size:    var(--uwc-paginator-label-size, 0.8125rem);
      color:        ${t.textSecondary};
      margin-inline: 0.375rem;
      white-space:  nowrap;
    }

    /* ── Jump to page ───────────────────────────────────────────────────── */
    .uwc-pg__jump {
      display:     flex;
      align-items: center;
      gap:         0.375rem;
      margin-inline: 0.375rem;
    }
    .uwc-pg__jump-label {
      font-size:  var(--uwc-paginator-label-size, 0.8125rem);
      color:      ${t.textSecondary};
      white-space: nowrap;
    }
    .uwc-pg__jump-input {
      width:         3.5rem;
      height:        2.25rem;
      padding-inline: 0.5rem;
      border:        1px solid ${t.borderControl};
      border-radius: var(--uwc-paginator-btn-radius, ${t.radiusMd});
      background:    ${t.surface};
      color:         ${t.text};
      font-family:   inherit;
      font-size:     inherit;
      text-align:    center;
      outline:       none;
      appearance:    textfield;
      -moz-appearance: textfield;
      transition:    border-color ${t.durationBase};
    }
    .uwc-pg__jump-input::-webkit-inner-spin-button,
    .uwc-pg__jump-input::-webkit-outer-spin-button { appearance: none; }
    .uwc-pg__jump-input:focus {
      border-color: var(--uwc-color-primary, ${t.primary});
    }
    .uwc-pg__jump-select {
      --uwc-dd-trigger-min-width: 4.5rem;
    }

    /* ── Slots ──────────────────────────────────────────────────────────── */
    .uwc-pg__slot-start,
    .uwc-pg__slot-end {
      display:     flex;
      align-items: center;
    }
  `,
];
